import { useState, useRef, ChangeEvent } from 'react'
import { router } from '@inertiajs/react'
import { Send, User as UserIcon, Paperclip, FileIcon, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import AttachmentPicker from './AttachmentPicker'
import { toast } from 'sonner'
import { useOrgPath } from '@/lib/pathFor'

interface Attachment {
  id: string
  filename: string
  mime_type: string
  size: number
}

interface PendingFile {
  id: string
  file: File
  name: string
  size: number
}

const MAX_FILE_SIZE = 25 * 1024 * 1024 // 25MB

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

interface DraftEditorProps {
  // Edit mode: existing draft
  draft?: {
    id: string
    body: string
  }
  // Compose mode: creating a new draft reply in a thread
  emailThreadId?: string
  replyToEmailId?: string
  // Recipient info (shown in the card header)
  to?: string
  cc?: string | null
  bcc?: string | null
  // Header content (rendered above the editor, e.g. provenance badge row)
  header?: React.ReactNode
  // Attachments (only for edit mode — compose mode stages files locally)
  attachments?: Attachment[]
  onAttachmentsChange?: (attachments: Attachment[]) => void
  // Navigation
  filter?: string
  attention?: string | null
  search?: string
  cursor?: string | null
  // Callbacks
  onClose: () => void
}

export default function DraftEditor({
  draft,
  emailThreadId,
  replyToEmailId,
  to,
  cc,
  bcc,
  header,
  attachments = [],
  onAttachmentsChange,
  filter,
  attention,
  search,
  cursor,
  onClose,
}: DraftEditorProps) {
  const [body, setBody] = useState(draft?.body || '')
  const [saving, setSaving] = useState(false)
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pathFor = useOrgPath()

  const isEditMode = !!draft

  function buildQueryParams() {
    const params: Record<string, string> = {}
    if (filter) params.filter = filter
    if (attention) params.attention = attention
    if (search) params.search = search
    if (cursor) params.cursor = cursor
    return params
  }

  // --- Compose-mode local file staging ---

  function handleFileSelect(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File too large (max 25MB)')
      return
    }
    setPendingFiles(prev => [...prev, {
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: file.size,
    }])
    e.target.value = ''
  }

  function removePendingFile(id: string) {
    setPendingFiles(prev => prev.filter(f => f.id !== id))
  }

  async function uploadPendingFiles(emailId: string): Promise<string[]> {
    const errors: string[] = []
    for (const pf of pendingFiles) {
      const formData = new FormData()
      formData.append('file', pf.file)
      try {
        const resp = await fetch(pathFor(`/emails/${emailId}/attachments`), {
          method: 'POST',
          body: formData,
          headers: { 'X-CSRF-Token': csrfToken() },
        })
        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}))
          errors.push(`${pf.name}: ${data.error || 'upload failed'}`)
        }
      } catch {
        errors.push(`${pf.name}: network error`)
      }
    }
    return errors
  }

  // --- Compose-mode: create draft via fetch (returns email ID for file uploads) ---

  async function createDraftViaFetch(sendImmediately: boolean) {
    if (!body.trim()) {
      toast.error('Email body cannot be empty')
      return
    }

    setSaving(true)
    try {
      // Always create as draft first so we can upload attachments
      const resp = await fetch(pathFor('/emails'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
        body: JSON.stringify({
          email_thread_id: emailThreadId,
          reply_to_email_id: replyToEmailId,
          to,
          cc,
          bcc,
          body,
        }),
      })

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}))
        toast.error(data.error || 'Failed to create draft')
        setSaving(false)
        return
      }

      const { id: emailId, email_thread_id } = await resp.json()

      // Upload staged files
      if (pendingFiles.length > 0) {
        const uploadErrors = await uploadPendingFiles(emailId)
        if (uploadErrors.length > 0) {
          toast.error(`Some attachments failed: ${uploadErrors.join('; ')}`)
        }
      }

      // Send if requested
      if (sendImmediately) {
        const sendResp = await fetch(pathFor(`/emails/${emailId}/send`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken(),
          },
        })
        if (!sendResp.ok) {
          const data = await sendResp.json().catch(() => ({}))
          toast.error(data.error || 'Draft created but failed to send — you can retry from the thread')
        }
      }

      onClose()
      // Reload the thread to show the new draft/sent email
      router.visit(pathFor(`/inbox/threads/${email_thread_id}?${new URLSearchParams(buildQueryParams()).toString()}`), {
        preserveScroll: true,
      })
    } catch {
      toast.error('Failed to create email')
      setSaving(false)
    }
  }

  // --- Actions ---

  function handleDiscard() {
    if (isEditMode) {
      if (confirm('Discard this draft?')) {
        router.post(pathFor(`/emails/${draft!.id}/discard`), buildQueryParams(), {
          preserveScroll: true,
          onError: () => toast.error('Failed to discard draft'),
        })
      }
    } else {
      onClose()
    }
  }

  function handleSaveDraft() {
    if (isEditMode) {
      if (!body.trim()) {
        toast.error('Email body cannot be empty')
        return
      }
      setSaving(true)
      router.patch(pathFor(`/emails/${draft!.id}`), { body, ...buildQueryParams() }, {
        preserveScroll: true,
        onFinish: () => setSaving(false),
        onSuccess: () => onClose(),
        onError: () => toast.error('Failed to save draft'),
      })
    } else {
      createDraftViaFetch(false)
    }
  }

  function handleSendEmail() {
    if (isEditMode) {
      if (!body.trim()) {
        toast.error('Email body cannot be empty')
        return
      }
      setSaving(true)
      router.patch(pathFor(`/emails/${draft!.id}`), { body, ...buildQueryParams() }, {
        preserveScroll: true,
        onSuccess: () => {
          router.post(pathFor(`/emails/${draft!.id}/send`), buildQueryParams(), {
            preserveScroll: true,
            onError: () => {
              toast.error('Failed to send email')
              setSaving(false)
            },
          })
        },
        onError: () => {
          toast.error('Failed to save draft')
          setSaving(false)
        },
      })
    } else {
      createDraftViaFetch(true)
    }
  }

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/[0.02] p-5 space-y-4">
      {/* Header (provenance badges, etc.) */}
      {header || (
        <div className="flex items-center gap-2">
          <UserIcon className="h-4 w-4 text-muted-foreground" />
          <span className="text-[13px] font-semibold text-foreground">New reply</span>
        </div>
      )}

      {/* Recipient line */}
      {to && (
        <div className="text-xs text-muted-foreground">
          To: {to}
          {cc && <> &middot; CC: {cc}</>}
          {bcc && <> &middot; BCC: {bcc}</>}
        </div>
      )}

      {/* Body */}
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Write your reply..."
        className="min-h-[200px] text-[13px] leading-[1.6]"
        autoFocus
      />

      {/* Attachments */}
      {isEditMode && onAttachmentsChange ? (
        // Edit mode: real attachments with upload/remove via API
        <AttachmentPicker
          emailId={draft!.id}
          attachments={attachments}
          onAttachmentsChange={onAttachmentsChange}
          disabled={false}
        />
      ) : (
        // Compose mode: locally staged files
        <div className="space-y-2">
          {pendingFiles.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {pendingFiles.map((pf) => (
                <div
                  key={pf.id}
                  className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-1.5 text-xs"
                >
                  <FileIcon className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="max-w-[150px] truncate">{pf.name}</span>
                  <span className="text-muted-foreground">({formatFileSize(pf.size)})</span>
                  <button
                    onClick={() => removePendingFile(pf.id)}
                    className="ml-1 text-muted-foreground hover:text-red-600 cursor-pointer"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip className="h-3 w-3 mr-1.5" />
              Attach File
            </Button>
            <span className="text-[10px] text-muted-foreground">Max 25MB per file</span>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center justify-end gap-2">
        <button
          className="text-xs text-muted-foreground hover:text-foreground cursor-pointer"
          onClick={onClose}
        >
          Cancel
        </button>
        <Button variant="outline" size="sm" className="h-7 text-xs cursor-pointer" onClick={handleDiscard}>
          Discard
        </Button>
        <Button variant="outline" size="sm" className="h-7 text-xs cursor-pointer" onClick={handleSaveDraft} disabled={saving}>
          {saving ? 'Saving...' : 'Save Draft'}
        </Button>
        <Button
          size="sm"
          className="h-7 text-xs bg-black hover:bg-gray-800 text-white cursor-pointer"
          onClick={handleSendEmail}
          disabled={saving}
        >
          <Send className="size-3" />
          Send Email
        </Button>
      </div>
    </div>
  )
}
